import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  $countriesStore,
  $recipientCreateStore,
  $recipientUpdateStore,
  $transferReasonsStore,
  $transferRequestsCreateStore,
  $transferRequestsFeeStore,
  $transferRequestsUpdateStore,
  $transferRequestsGetFxRateStore,
  $recipientMandatoryFieldsStore,
} from '@lib/store';
import { AmountUtils, cn, PaletteUtils, PaymentUtils } from '@shared/utils';
import {
  AmountTypeEnum,
  TransferRecipientTypeEnum,
  TransferRequestTypeEnum,
  type AccountResponse,
  type ApiResponse,
  type RecipientResponse,
} from '@shared/api';
import {
  AccountFormBlock,
  AddressFormBlock,
  FilesFormBlock,
  PaymentFormBlock,
  PersonalFormBlock,
  RecipientSelectBlock,
  TransferConfirmationBlock,
  type AccountFormData,
  type AddressFormData,
  type FilesFormData,
  type PaymentFormData,
  type PersonalFormData,
} from '@widgets';
import { Button, type FileWithId, type CustomFile, Spinner } from '@shared/widgets';

type IFormDataIndividualBetween = PersonalFormData &
  AddressFormData &
  AccountFormData &
  PaymentFormData &
  FilesFormData & { recipient?: RecipientResponse | null };
const DEFAULT_VALUES: IFormDataIndividualBetween = {
  firstName: '',
  lastName: '',
  idNumber: '',
  email: '',
  address: '',
  postCode: '',
  city: '',
  province: '',
  companyCountry: '',
  iban: '',
  currency: '',
  bankName: '',
  bankCountry: '',
  accountNumber: '',
  bic: '',
  accountType: undefined,
  bankProvince: '',
  bankAddress: '',
  bankCity: '',
  branchCode: '',
  amountType: AmountTypeEnum.BeneficiaryAmount,
  amount: undefined,
  beneficiaryAmount: undefined,
  paymentReason: '',
  purpose: '',
  fastPayment: true,
  files: [] as (FileWithId | CustomFile)[],
};

interface OtherAccountIndividualTabProps {
  account: AccountResponse | null;
}

export const OtherAccountIndividualTab = (props: OtherAccountIndividualTabProps) => {
  const countryStore = useUnit($countriesStore.store);
  const transferReasonsStore = useUnit($transferReasonsStore.store);
  const feeStore = useUnit($transferRequestsFeeStore.store);
  const transferRequestsCreateStore = useUnit($transferRequestsCreateStore.store);

  const [isShowRecipientDetails, setIsShowRecipientDetails] = useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [createdRecipientData, setCreatedRecipientData] = useState<RecipientResponse | null>(null);
  const [recipientId, setRecipientId] = useState<ApiResponse<RecipientResponse>>();

  const formId = useId();
  const formControl = useForm<IFormDataIndividualBetween>({ defaultValues: DEFAULT_VALUES });
  const recipient = formControl.watch('recipient');
  const amount = formControl.watch('amount');
  const beneficiaryAmount = formControl.watch('beneficiaryAmount');
  const companyCountry = formControl.watch('companyCountry');
  const currency = formControl.watch('currency');
  const bankCountry = formControl.watch('bankCountry');
  const fastPayment = formControl.watch('fastPayment');

  const { t } = useTranslation('payments');

  useEffect(() => {
    return () => {
      $recipientMandatoryFieldsStore.events.clear();
      $transferRequestsCreateStore.events.clear();
      $transferRequestsGetFxRateStore.events.clear();
    };
  }, []);

  useEffect(() => {
    if (!recipient) return;
    setIsShowRecipientDetails(true);
  }, [recipient]);

  const requestFeeHandler = useCallback(() => {
    if (amount && (recipient?.bankCountryCode || bankCountry)) {
      $transferRequestsFeeStore.events.getFee({
        AccountId: props.account?.id,
        SenderAmount: amount,
        FastPayment: fastPayment,
        BeneficiaryCurrencyCode: recipient?.currency || currency,
        BeneficiaryBankCountryCode: recipient?.bankCountryCode || bankCountry,
        TransferRequestType: TransferRequestTypeEnum.OtherInsigniaAccount,
      });
    }
  }, [amount, bankCountry, currency, fastPayment, recipient, props.account?.id]);
  useEffect(() => {
    requestFeeHandler();
    return () => $transferRequestsFeeStore.events.clear();
  }, [props.account?.id, recipient, currency, bankCountry]);

  const countryOptions = useMemo(() => countryStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode })), [countryStore]);
  const BankCountryName = countryOptions.find((option) => option.value === bankCountry);
  const ResidenceCountryName = countryOptions.find((option) => option.value === companyCountry);
  useEffect(() => {
    if (BankCountryName && ResidenceCountryName && currency) {
      $recipientMandatoryFieldsStore.events.getMandatoryFields({
        ResidenceCountryCode: ResidenceCountryName?.value,
        BankCountryCode: BankCountryName?.value,
        Currency: currency,
        Type: TransferRecipientTypeEnum.Individual,
      });
    }
  }, [BankCountryName, ResidenceCountryName, currency]);
  useEffect(() => {
    if (props.account?.id && props.account?.currency && (recipient?.currency || currency) && (bankCountry || recipient?.bankCountryCode)) {
      if (props.account.currency === (recipient?.currency || currency)) {
        $transferRequestsGetFxRateStore.events.setRate(1);
        return;
      }
      $transferRequestsGetFxRateStore.events.getFxRate({
        AccountId: props.account?.id,
        FromCurrency: props.account?.currency,
        ToCurrency: recipient?.currency || currency,
        CountryCode: bankCountry || recipient?.bankCountryCode,
      });
    }
  }, [props.account, currency, bankCountry, formControl.formState.isValid, recipient]);

  const onSubmit = async (data: IFormDataIndividualBetween) => {
    let recipientTmp = data.recipient;
    if (recipient == null) {
      const request = recipientId?.data ? await onUpdateRecipient(data) : await onCreateRecipient(data);
      recipientTmp = (request as unknown as { data: RecipientResponse | null }).data;
    }
    if (!recipientTmp || !props.account) return;
    setCreatedRecipientData(recipientTmp);

    if (transferRequestsCreateStore.data) {
      await $transferRequestsUpdateStore.events.updateTransfer({
        id: transferRequestsCreateStore?.data?.id,
        type: TransferRequestTypeEnum.OtherInsigniaAccount,
        fromAccountId: props.account.id,
        beneficiaryCountryCode: recipientTmp.countryCode,
        beneficiaryCurrency: recipientTmp.currency,
        recipientId: recipientTmp.id,
        senderAmount: data.amount,
        totalAmount: data.amount,
        beneficiaryAmount: data.beneficiaryAmount,
        reasonOfThePayment: data.paymentReason,
        purpose: data.purpose,
        fastPayment: data.fastPayment,
        StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
      });
      if (transferRequestsCreateStore.data?.id && data.files?.length) {
        const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: transferRequestsCreateStore.data.id, files: data.files });
        formControl.setValue('files', files);
      }
      setShowPaymentModal(true);
      return;
    }

    const newTransferRequest = await $transferRequestsCreateStore.events.createTransfer({
      type: TransferRequestTypeEnum.OtherInsigniaAccount,
      fromAccountId: props.account.id,
      beneficiaryCountryCode: recipientTmp.countryCode,
      beneficiaryCurrency: recipientTmp.currency,
      recipientId: recipientTmp.id,
      senderAmount: data.amount,
      totalAmount: data.amount,
      beneficiaryAmount: data.beneficiaryAmount,
      reasonOfThePayment: data.paymentReason,
      purpose: data.purpose,
      fastPayment: data.fastPayment,
      StickToBeneficiaryAmount: data.amountType === AmountTypeEnum.BeneficiaryAmount,
    });
    if (newTransferRequest.data?.id && data.files?.length) {
      const files = await PaymentUtils.uploadPaymentDocuments({ transferRequestId: newTransferRequest.data.id, files: data.files });
      formControl.setValue('files', files);
    }
    setShowPaymentModal(true);
  };

  const onCreateRecipient = async (data: IFormDataIndividualBetween) => {
    if (recipient != null) return null;

    const response = await $recipientCreateStore.events.createRecipient({
      type: TransferRecipientTypeEnum.Individual,
      countryCode: data.companyCountry,
      currency: data.currency!,
      accountNumber: data.accountNumber,
      accountType: data.accountType!,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      firstName: data.firstName,
      lastName: data.lastName,
      idNumber: data.idNumber,
      companyName: undefined,
      bankName: data.bankName,
      bankCountryCode: data.bankCountry,
      bankProvince: data.bankProvince,
      bankAddress: data.bankAddress,
      bankCity: data.bankCity,
      branchCode: data.branchCode,
      email: data.email,
      address: data.address,
      postCode: data.postCode,
      city: data.city,
      province: data.province,
    });
    setRecipientId(response);
    return response;
  };
  const onUpdateRecipient = async (data: IFormDataIndividualBetween) => {
    return await $recipientUpdateStore.events.updateRecipient({
      id: recipientId?.data?.id,
      type: TransferRecipientTypeEnum.Individual,
      countryCode: data.companyCountry,
      currency: data.currency,
      accountNumber: data.accountNumber,
      accountType: data.accountType,
      iban: data.iban,
      bicOrSwiftCode: data.bic,
      firstName: data.firstName,
      lastName: data.lastName,
      idNumber: data.idNumber,
      companyName: undefined,
      bankName: data.bankName,
      bankCountryCode: data.bankCountry,
      bankProvince: data.bankProvince,
      bankAddress: data.bankAddress,
      bankCity: data.bankCity,
      branchCode: data.branchCode,
      email: data.email,
      address: data.address,
      postCode: data.postCode,
      city: data.city,
      province: data.province,
    });
  };

  const reasonOptions = useMemo(() => {
    const bankCountryCode = (recipient ? recipient.bankCountryCode : bankCountry)?.toUpperCase();
    const individualReasons = transferReasonsStore.data.filter((reason) => reason?.beneficiaryType?.includes(TransferRecipientTypeEnum.Individual));
    const countryReasons = individualReasons.filter((reason) => reason.countryCode?.toUpperCase() == bankCountryCode);
    const reasons = countryReasons.length > 0 ? countryReasons : individualReasons.filter((reason) => !reason.countryCode);
    return reasons.map((reason) => ({ label: reason.name, value: reason.id }));
  }, [transferReasonsStore.data, bankCountry, recipient]);
  // const totalAmount = (amount || 0) + (feeStore.data || 0);
  return (
    <FormProvider {...formControl}>
      {formControl.formState.isSubmitting && <Spinner position />}
      <form id={formId} noValidate onSubmit={formControl.handleSubmit(onSubmit)}>
        <div className='bg-white p-6 dark:bg-soft-black'>
          <Controller
            name='recipient'
            control={formControl.control}
            rules={{
              validate: (value) => {
                if (value === undefined) return t('errors.form.required', { ns: 'common' });
                return true;
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <RecipientSelectBlock
                type={TransferRecipientTypeEnum.Individual}
                isInternalAccount
                recipient={value}
                onChangeRecipient={(recipient) => {
                  onChange(recipient);
                  formControl.setValue('purpose', '');
                }}
                isShowRecipientDetails={isShowRecipientDetails}
                onToggleRecipientDetails={() => setIsShowRecipientDetails((prev) => !prev)}
                error={error?.message}
              />
            )}
          />
          {recipient === null && (
            <div className='mt-6 flex flex-col gap-6'>
              <PersonalFormBlock />
              <AddressFormBlock />
              <AccountFormBlock />
            </div>
          )}
        </div>
        <PaymentFormBlock
          className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0')}
          account={props.account}
          currency={currency}
          reasonOptions={reasonOptions}
          onRequestFee={requestFeeHandler}
        />
        <FilesFormBlock
          className={cn('mt-6 bg-white p-6 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'mt-0 pt-0')}
          transactionId={transferRequestsCreateStore.data?.id}
        />
      </form>
      <div className={cn('mt-3', PaletteUtils.isVcfcPalette && 'mt-0 rounded-b-xl bg-white p-6 pt-0 dark:bg-soft-black')}>
        <Button
          className={cn('max-w-72', PaletteUtils.isVcfcPalette && 'w-[285px]')}
          color='primary'
          type='submit'
          form={formId}
          disabled={feeStore.data === undefined || feeStore.loading || transferRequestsCreateStore.loading || formControl.formState.isSubmitting}
        >
          {t('form.buttons.transfer')}{' '}
          {beneficiaryAmount && ` ${AmountUtils.transformToAmountWithCurrency(beneficiaryAmount, recipient ? recipient.currency : currency)}`}
        </Button>
      </div>
      {transferRequestsCreateStore.data?.id && (
        <TransferConfirmationBlock
          showPaymentModal={showPaymentModal}
          onClosePaymentModal={() => setShowPaymentModal(false)}
          account={props.account!}
          recipient={createdRecipientData}
        />
      )}
    </FormProvider>
  );
};
